import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ShowBase } from "ra-core";
import { Link, useNavigate, useParams } from "react-router";
import { CalendarDays, ChevronLeft, ChevronRight, Handshake, MapPin } from "lucide-react";

import { getSupabaseClient } from "../providers/supabase/supabase";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { MobilePage } from "../layout/MobilePage";
import { DealShowContent } from "./DealShow";
import { useSwipe } from "../misc/useSwipe";
import { EmptyState } from "../misc/EmptyState";

// LDF: Aufträge auf dem Handy – Liste nach Phase mit Jahres- und Phasenauswahl, Detail als eigene Seite.

type MobileDeal = {
  id: number;
  name: string;
  stage: string;
  amount: number | null;
  event_date: string | null;
  index: number;
  company: { name: string } | null;
  location: { name: string } | null;
};

const euro = (value: number | null) => (value == null ? "–" : `${Math.round(value).toLocaleString("de-DE")} €`);
const dateDe = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" }) : "ohne Datum");

const Chip = ({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) => (
  <button
    type="button"
    onClick={onClick}
    className={`h-9 shrink-0 rounded-full px-4 text-sm cursor-pointer ${active ? "bg-primary text-primary-foreground" : "border border-border bg-background"}`}
  >
    {children}
  </button>
);

const useMobileDeals = () =>
  useQuery({
    queryKey: ["mobile-deals"],
    queryFn: async () => {
      const { data, error } = await getSupabaseClient()
        .from("deals")
        .select("id,name,stage,amount,event_date,index,company:companies!deals_company_id_fkey(name),location:companies!deals_location_company_id_fkey(name)")
        .is("archived_at", null)
        .order("event_date", { ascending: false, nullsFirst: true });
      if (error) throw error;
      return (data ?? []) as unknown as MobileDeal[];
    },
  });

export const MobileDealList = () => {
  const { dealStages } = useConfigurationContext();
  const current = new Date().getFullYear();
  const [year, setYear] = useState<number | null>(null);
  const [stage, setStage] = useState<string>("");

  const { data, isPending } = useMobileDeals();

  const grouped = useMemo(() => {
    const rows = (data ?? []).filter(
      (d) => (!year || (d.event_date ?? "").startsWith(String(year))) && (!stage || d.stage === stage),
    );
    return dealStages
      .map((s) => ({ stage: s, deals: rows.filter((d) => d.stage === s.value) }))
      .filter((g) => g.deals.length);
  }, [data, year, stage, dealStages]);

  return (
    <MobilePage title="Aufträge">
      <div className="-mx-4 mb-2 flex gap-2 overflow-x-auto px-4 pb-1">
        {[current, current - 1, current - 2].map((y) => (
          <Chip key={y} active={year === y} onClick={() => setYear(year === y ? null : y)}>
            {y}
          </Chip>
        ))}
      </div>
      <div className="-mx-4 mb-4 flex gap-2 overflow-x-auto px-4 pb-1">
        <Chip active={!stage} onClick={() => setStage("")}>
          Alle Phasen
        </Chip>
        {dealStages.map((s) => (
          <Chip key={s.value} active={stage === s.value} onClick={() => setStage(stage === s.value ? "" : s.value)}>
            {s.label}
          </Chip>
        ))}
      </div>

      {isPending ? (
        <p className="py-6 text-sm text-muted-foreground">Wird geladen …</p>
      ) : !grouped.length ? (
        <EmptyState Icon={Handshake} title="Keine Aufträge für diese Auswahl" text="Anderes Jahr oder „Alle Phasen“ wählen." />
      ) : (
        grouped.map((g) => (
          <section key={g.stage.value} className="mb-5">
            <h2 className="mb-1 flex items-baseline justify-between text-sm font-semibold">
              {g.stage.label}
              <span className="text-xs font-normal text-muted-foreground">
                {g.deals.length} · {euro(g.deals.reduce((sum, d) => sum + (d.amount ?? 0), 0))}
              </span>
            </h2>
            <ul className="divide-y divide-border rounded-lg border border-border bg-card">
              {g.deals.map((d) => (
                <li key={d.id}>
                  <Link to={`/deals/${d.id}/show`} className="flex items-center gap-3 px-3 py-3 no-underline">
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium">{d.name}</div>
                      <div className="truncate text-xs text-muted-foreground">{d.company?.name}</div>
                      <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-muted-foreground">
                        <span className="inline-flex items-center gap-1">
                          <CalendarDays className="h-3 w-3" /> {dateDe(d.event_date)}
                        </span>
                        {d.location && (
                          <span className="inline-flex min-w-0 items-center gap-1">
                            <MapPin className="h-3 w-3 shrink-0" /> <span className="truncate">{d.location.name}</span>
                          </span>
                        )}
                      </div>
                    </div>
                    <span className="shrink-0 text-sm">{euro(d.amount)}</span>
                    <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        ))
      )}
    </MobilePage>
  );
};

export const MobileDealShow = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { dealStages } = useConfigurationContext();
  const { data } = useMobileDeals();

  // Reihenfolge wie in der Liste: nach Phase, darin nach Eventdatum
  const ordered = useMemo(
    () => dealStages.flatMap((s) => (data ?? []).filter((d) => d.stage === s.value)),
    [data, dealStages],
  );
  const index = ordered.findIndex((d) => String(d.id) === String(id));
  const prev = index > 0 ? ordered[index - 1] : undefined;
  const next = index >= 0 ? ordered[index + 1] : undefined;
  const go = (deal?: MobileDeal) => deal && navigate(`/deals/${deal.id}/show`, { replace: true });

  useSwipe({ onLeft: () => go(next), onRight: () => go(prev) });

  return (
    <MobilePage title="Auftrag" back="/deals">
      {index >= 0 && (
        <div className="-mt-1 mb-2 flex items-center justify-between text-xs text-muted-foreground">
          <button type="button" disabled={!prev} onClick={() => go(prev)} className="inline-flex h-10 items-center gap-1 pr-3 disabled:opacity-40 cursor-pointer">
            <ChevronLeft className="h-4 w-4" /> Zurück
          </button>
          <span>
            ← wischen · {index + 1} / {ordered.length} →
          </span>
          <button type="button" disabled={!next} onClick={() => go(next)} className="inline-flex h-10 items-center gap-1 pl-3 disabled:opacity-40 cursor-pointer">
            Weiter <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}
      <ShowBase resource="deals" id={id}>
        <DealShowContent mobile />
      </ShowBase>
    </MobilePage>
  );
};
